import React, { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Full list of GSTIN state codes (India)
export const INDIAN_STATES = [
  { code: "01", name: "Jammu and Kashmir" },
  { code: "02", name: "Himachal Pradesh" },
  { code: "03", name: "Punjab" },
  { code: "04", name: "Chandigarh" },
  { code: "05", name: "Uttarakhand" },
  { code: "06", name: "Haryana" },
  { code: "07", name: "Delhi" },
  { code: "08", name: "Rajasthan" },
  { code: "09", name: "Uttar Pradesh" },
  { code: "10", name: "Bihar" },
  { code: "11", name: "Sikkim" },
  { code: "12", name: "Arunachal Pradesh" },
  { code: "13", name: "Nagaland" },
  { code: "14", name: "Manipur" },
  { code: "15", name: "Mizoram" },
  { code: "16", name: "Tripura" },
  { code: "17", name: "Meghalaya" },
  { code: "18", name: "Assam" },
  { code: "19", name: "West Bengal" },
  { code: "20", name: "Jharkhand" },
  { code: "21", name: "Odisha" },
  { code: "22", name: "Chhattisgarh" },
  { code: "23", name: "Madhya Pradesh" },
  { code: "24", name: "Gujarat" },
  { code: "26", name: "Dadra and Nagar Haveli and Daman and Diu" },
  { code: "27", name: "Maharashtra" },
  { code: "28", name: "Andhra Pradesh (old)" },
  { code: "29", name: "Karnataka" },
  { code: "30", name: "Goa" },
  { code: "31", name: "Lakshadweep" },
  { code: "32", name: "Kerala" },
  { code: "33", name: "Tamil Nadu" },
  { code: "34", name: "Puducherry" },
  { code: "35", name: "Andaman and Nicobar Islands" },
  { code: "36", name: "Telangana" },
  { code: "37", name: "Andhra Pradesh" },
  { code: "38", name: "Ladakh" },
  { code: "97", name: "Other Territory" },
  { code: "99", name: "Centre Jurisdiction" },
];

/**
 * Combined state selector — a dropdown listing all Indian states with their GST codes
 * plus a "Custom / Manual" option that reveals raw text inputs for edge cases (foreign
 * customers, non-standard entries). Sets both `state` and `state_code` in one action.
 */
export default function StateSelector({ state = "", stateCode = "", onChange, testid = "state-select" }) {
  const match = useMemo(
    () => INDIAN_STATES.find((s) => s.code === stateCode) ||
          INDIAN_STATES.find((s) => s.name === state),
    [state, stateCode],
  );

  const value = match ? match.code : (state || stateCode ? "__manual" : "");

  const pick = (v) => {
    if (v === "__manual") {
      onChange({ state: state || "", state_code: stateCode || "" });
      return;
    }
    const s = INDIAN_STATES.find((x) => x.code === v);
    if (s) onChange({ state: s.name, state_code: s.code });
  };

  const isManual = value === "__manual";

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5 col-span-2">
        <Label className="eyebrow">State</Label>
        <Select value={value} onValueChange={pick}>
          <SelectTrigger data-testid={testid}>
            <SelectValue placeholder="Pick a state (or Manual)" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {INDIAN_STATES.map((s) => (
              <SelectItem key={s.code} value={s.code} data-testid={`${testid}-opt-${s.code}`}>
                <span className="font-mono text-[11px] mr-2 text-muted-foreground">{s.code}</span>
                {s.name}
              </SelectItem>
            ))}
            <SelectItem value="__manual" data-testid={`${testid}-opt-manual`}>
              <span className="font-mono text-[11px] mr-2 text-muted-foreground">✎</span>
              Custom / Manual entry
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      {isManual && (
        <>
          <div className="space-y-1.5">
            <Label className="eyebrow">State (manual)</Label>
            <Input
              value={state} onChange={(e) => onChange({ state: e.target.value, state_code: stateCode })}
              placeholder="e.g. Bhutan"
              data-testid={`${testid}-manual-state`}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="eyebrow">State Code</Label>
            <Input
              value={stateCode} onChange={(e) => onChange({ state, state_code: e.target.value })}
              className="font-mono" placeholder="00"
              data-testid={`${testid}-manual-code`}
            />
          </div>
        </>
      )}
    </div>
  );
}
